import { useState, useEffect } from 'react';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { Modal } from '../../../components/Modal';
import { UserLinks } from '../../../components/UserLinks';
import LoadingSpinner from '../../../components/LoadingSpinner';
import type { ObsessLessUser } from '../../../types/commonTypes';
import { referralCodesService } from '../services/referralCodesService';

interface UsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  code: string;
  codeName: string;
}

export const UsersModal = ({ isOpen, onClose, code, codeName }: UsersModalProps) => {
  const [users, setUsers] = useState<ObsessLessUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastDocs, setLastDocs] = useState<(QueryDocumentSnapshot | null)[]>([null]);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);
  const [filterFinishedOnboarding, setFilterFinishedOnboarding] = useState(false);
  const pageSize = 50;

  const fetchCount = async (finishedOnboarding: boolean) => {
    setLoadingCount(true);
    try {
      const count = await referralCodesService.getUsersCountByReferralCode(code, finishedOnboarding || undefined);
      setTotalCount(count);
    } catch (err) {
      console.error('Failed to fetch count:', err);
    } finally {
      setLoadingCount(false);
    }
  };

  const fetchUsers = async (page: number, finishedOnboarding: boolean) => {
    setLoading(true);
    setError('');
    try {
      const lastDoc = lastDocs[page - 1];
      const result = await referralCodesService.getUsersByReferralCode(
        code,
        pageSize,
        lastDoc || undefined,
        finishedOnboarding || undefined
      );
      setUsers(result.users);
      setHasMore(result.hasMore);

      if (result.lastDoc && !lastDocs[page]) {
        setLastDocs((prev) => {
          const newDocs = [...prev];
          newDocs[page] = result.lastDoc;
          return newDocs;
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && code) {
      setCurrentPage(1);
      setLastDocs([null]);
      setTotalCount(0);
      fetchCount(filterFinishedOnboarding);
      fetchUsers(1, filterFinishedOnboarding);
    }
  }, [isOpen, code]);

  useEffect(() => {
    if (isOpen && code) {
      setCurrentPage(1);
      setLastDocs([null]);
      setTotalCount(0);
      fetchCount(filterFinishedOnboarding);
      fetchUsers(1, filterFinishedOnboarding);
    }
  }, [filterFinishedOnboarding]);

  const handlePageChange = async (page: number) => {
    if (page === currentPage || loading) return;
    setCurrentPage(page);
    await fetchUsers(page, filterFinishedOnboarding);
  };

  const handleFinishedOnboardingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterFinishedOnboarding(e.target.checked);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleString();
  };

  const totalPages = Math.min(Math.ceil(totalCount / pageSize), 2); // Max 2 pages
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCount);
  const showLimitWarning = totalCount > 100;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Users with code ${codeName}`} size="xl">
      <div className="space-y-4">
        {/* Header with count and filters */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm text-gray-600">
              {loadingCount ? (
                'Loading count...'
              ) : totalCount === 0 ? (
                'No users found'
              ) : (
                <>
                  <span className="font-semibold text-gray-900">{totalCount}</span> user{totalCount !== 1 ? 's' : ''} found
                </>
              )}
            </p>
            {!loading && users.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Showing {startIndex}-{endIndex} of {totalCount}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filterFinishedOnboarding}
                onChange={handleFinishedOnboardingChange}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
              />
              Finished onboarding
            </label>
            {showLimitWarning && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                Displaying first 100 users only
              </span>
            )}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading && users.length === 0 ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : users.length === 0 && !loading ? (
          <div className="text-center py-12 text-gray-500">
            No users have used this code yet
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Display Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Journey Day
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Onboarding
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.uid} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="truncate max-w-[150px]" title={user.uid}>
                              {user.uid}
                            </span>
                            <UserLinks userId={user.uid} mode="compact" />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="truncate max-w-[200px]" title={user.email || ''}>
                            {user.email || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {user.displayName || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.questJourney ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                              Day {user.questJourney.currentDay}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.onboardingProgress && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
                              {user.onboardingProgress}
                            </span>
                          )}
                          {!user.onboardingProgress && (
                            <span className="text-red-400">Not started</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-wrap justify-center items-center gap-2 pt-4">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    disabled={loading}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasMore || loading || currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
